import { useEffect, useState } from "react";
import { Download, Share, PlusSquare, MoreVertical, X, Smartphone } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

let deferred = null;
const subs = new Set();
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferred = e;
    subs.forEach((f) => f());
  });
  window.addEventListener("appinstalled", () => {
    deferred = null;
    subs.forEach((f) => f());
  });
}

const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
export const isIOS = /iphone|ipad|ipod/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
const isInApp = /Telegram|FBAN|FBAV|Instagram|WhatsApp/i.test(ua);
export const isStandalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

function useInstall() {
  const [, force] = useState(0);
  useEffect(() => {
    const f = () => force((n) => n + 1);
    subs.add(f);
    return () => { subs.delete(f); };
  }, []);
  return deferred;
}

const Step = ({ n, icon: Icon, children }) => (
  <li className="flex gap-4 items-start rounded-xl border border-white/[0.07] bg-white/[0.02] p-4">
    <span className="w-8 h-8 shrink-0 rounded-full bg-gold text-noir text-sm font-bold flex items-center justify-center">{n}</span>
    <p className="text-sm leading-relaxed pt-1">{children} {Icon && <Icon className="inline w-4 h-4 text-gold align-[-3px]" />}</p>
  </li>
);

const IOSSteps = () => (
  <ol className="space-y-3 mt-5">
    <Step n={1} icon={Share}>Apri questo sito con <strong>Safari</strong> e tocca il pulsante <strong>Condividi</strong></Step>
    <Step n={2} icon={PlusSquare}>Scorri e scegli <strong>«Aggiungi alla schermata Home»</strong></Step>
    <Step n={3}>Tocca <strong>Aggiungi</strong> in alto a destra: l'icona oro Skin Lovers comparirà tra le tue app</Step>
  </ol>
);

const AndroidSteps = () => (
  <ol className="space-y-3 mt-5">
    <Step n={1} icon={MoreVertical}>Apri questo sito con <strong>Chrome</strong> e tocca il menu in alto a destra</Step>
    <Step n={2}>Scegli <strong>«Installa app»</strong> oppure <strong>«Aggiungi a schermata Home»</strong></Step>
    <Step n={3}>Conferma con <strong>Installa</strong>: l'icona oro Skin Lovers comparirà tra le tue app</Step>
  </ol>
);

export function InstallHelpDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="install-help-dialog" className="bg-ivory-card rounded-2xl border-gold/30 w-[calc(100%-1.5rem)] max-w-md max-h-[88vh] overflow-y-auto">
        <img src="/icon-192.png" alt="" className="w-14 h-14 rounded-2xl border border-gold/40" />
        <DialogTitle className="font-serif text-3xl font-light leading-tight mt-2">Metti <em className="text-gold-gradient">My Skin Lovers</em> sul telefono</DialogTitle>
        <DialogDescription className="text-warm-text">Si apre a tutto schermo come una vera app, con l'icona sulla schermata Home.</DialogDescription>
        {isInApp && (
          <p data-testid="install-inapp-warning" className="mt-4 rounded-xl border border-gold/40 bg-gold/10 px-4 py-3 text-sm text-gold-light">
            Stai usando il browser interno di un'app (es. Telegram). Tocca i tre puntini e scegli «Apri nel browser», poi segui i passi.
          </p>
        )}
        {isIOS ? <IOSSteps /> : <AndroidSteps />}
      </DialogContent>
    </Dialog>
  );
}

export function InstallButton({ className = "btn-ghost", label = "Installa l'app", testid = "install-app-btn" }) {
  const prompt = useInstall();
  const [help, setHelp] = useState(false);
  if (isStandalone()) return null;
  const go = async () => {
    if (prompt) {
      prompt.prompt();
      await prompt.userChoice.catch(() => {});
      deferred = null;
      subs.forEach((f) => f());
    } else {
      setHelp(true);
    }
  };
  return (
    <>
      <button data-testid={testid} onClick={go} className={className}><Download className="w-4 h-4" /> {label}</button>
      <InstallHelpDialog open={help} onOpenChange={setHelp} />
    </>
  );
}

const KEY = "msl-install-dismissed";

export function InstallBanner() {
  const [hidden, setHidden] = useState(() => Number(localStorage.getItem(KEY) || 0) > Date.now());
  if (hidden || isStandalone()) return null;
  const close = () => {
    localStorage.setItem(KEY, String(Date.now() + 7 * 864e5));
    setHidden(true);
  };
  return (
    <div data-testid="install-banner" className="relative mb-8 rounded-2xl border border-gold/40 bg-gradient-to-r from-gold/[0.14] to-gold/[0.03] p-4 sm:p-5 flex items-center gap-4 animate-fade-up">
      <span className="hidden sm:flex w-11 h-11 shrink-0 rounded-full border border-gold/40 bg-gold/10 items-center justify-center"><Smartphone className="w-5 h-5 text-gold" strokeWidth={1.5} /></span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">Installa l'app sul telefono</p>
        <p className="text-xs text-warm-text mt-0.5">La apri con un tocco dalla schermata Home, come le altre app.</p>
      </div>
      <InstallButton className="btn-gold h-10 px-4 text-[10px] shrink-0" label="Installa" testid="install-banner-btn" />
      <button data-testid="install-banner-close" onClick={close} aria-label="Chiudi" className="absolute -top-1 -right-1 w-9 h-9 flex items-center justify-center text-warm-text hover:text-white"><X className="w-4 h-4" /></button>
    </div>
  );
}
